import React, { useCallback, useEffect, useMemo } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { useAppDispatch, useAppSelector } from '../../../store/store';
import { fpColors } from '../../../styles/theme';
import {
  loadGroupActivity,
  selectGroupActivity,
} from '../groupsSlice';
import type {
  CustodyAction,
  GroupActivityEvent,
} from '../../../api/types';

/**
 * GroupActivityTab — Sprint 4 / Chunk 10B · FE-10.6.
 *
 * Paginated activity feed for a single group. Renders events grouped
 * by day ("Today" / "Yesterday" / "Apr 28") with a small icon, the
 * actor → recipient line, the item name, and the relative timestamp.
 *
 * Empty / loading / error states are handled inline. Pull-to-refresh
 * + infinite scroll mirror the GroupInventorySection pattern (FE-9.3).
 *
 * The backend pre-joins user + item names so the component reads
 * straight from the cached events — no cross-slice lookups required.
 */

const PAGE_SIZE = 30;

export interface GroupActivityTabProps {
  groupId: number;
}

export const GroupActivityTab: React.FC<GroupActivityTabProps> = ({
  groupId,
}) => {
  const dispatch = useAppDispatch();
  const cache = useAppSelector(selectGroupActivity(groupId));

  const events = cache?.events ?? [];
  const loading = cache?.loading ?? false;
  const hasLoaded = cache?.hasLoaded ?? false;
  const total = cache?.total ?? 0;
  const nextOffset = cache?.nextOffset ?? 0;
  const error = cache?.error ?? null;
  const hasMore = events.length < total;

  // Initial load — fire on mount if we've never loaded this group's
  // feed before. Subsequent visits use the cache.
  useEffect(() => {
    if (!hasLoaded && !loading) {
      dispatch(
        loadGroupActivity({ groupId, limit: PAGE_SIZE, offset: 0, mode: 'replace' }),
      );
    }
    // Intentionally tracking only groupId; the slice's hasLoaded flag
    // prevents re-fetching on re-mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const handleRefresh = useCallback(() => {
    dispatch(
      loadGroupActivity({ groupId, limit: PAGE_SIZE, offset: 0, mode: 'replace' }),
    );
  }, [dispatch, groupId]);

  const handleLoadMore = useCallback(() => {
    if (loading || !hasMore) return;
    dispatch(
      loadGroupActivity({
        groupId,
        limit: PAGE_SIZE,
        offset: nextOffset,
        mode: 'append',
      }),
    );
  }, [dispatch, groupId, loading, hasMore, nextOffset]);

  const sections = useMemo(() => groupByDayBucket(events), [events]);

  /* ── render branches ──────────────────────────────────────────── */

  if (!hasLoaded && loading) {
    return (
      <View style={styles.section}>
        <View style={styles.headerRow}>
          <Text style={styles.sectionLabel}>Activity</Text>
        </View>
        <View style={styles.skeletonWrap}>
          {[0, 1, 2, 3].map((i) => (
            <View key={i} style={styles.skeletonRow} />
          ))}
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.section}>
        <View style={styles.headerRow}>
          <Text style={styles.sectionLabel}>Activity</Text>
        </View>
        <View style={styles.errorBanner}>
          <MaterialIcons
            name="error-outline"
            size={18}
            color={fpColors.error}
            style={{ marginRight: 8 }}
          />
          <Text style={styles.errorText} numberOfLines={2}>
            {error}
          </Text>
          <Pressable
            onPress={handleRefresh}
            accessibilityRole="button"
            accessibilityLabel="Retry loading activity"
            style={styles.errorRetry}
          >
            <Text style={styles.errorRetryText}>Retry</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  if (hasLoaded && events.length === 0) {
    return (
      <View style={styles.section}>
        <View style={styles.headerRow}>
          <Text style={styles.sectionLabel}>Activity</Text>
          <Text style={styles.headerCount}>0 events</Text>
        </View>
        <View style={styles.emptyWrap}>
          <Text style={styles.emptyEmoji}>📜</Text>
          <Text style={styles.emptyTitle}>No activity yet</Text>
          <Text style={styles.emptyBody}>
            Hand off, return, or recall an item — the timeline appears here.
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.section}>
      <View style={styles.headerRow}>
        <Text style={styles.sectionLabel}>Activity</Text>
        <Text style={styles.headerCount}>
          {total === 1 ? '1 event' : `${total} events`}
        </Text>
      </View>

      <FlatList
        data={sections}
        keyExtractor={(s) => s.bucketKey}
        contentContainerStyle={styles.list}
        refreshControl={
          <RefreshControl
            refreshing={loading && hasLoaded}
            onRefresh={handleRefresh}
            tintColor={fpColors.primary}
          />
        }
        onEndReached={handleLoadMore}
        onEndReachedThreshold={0.4}
        ListFooterComponent={
          loading && hasLoaded ? (
            <View style={styles.footerLoading}>
              <ActivityIndicator size="small" color={fpColors.primary} />
            </View>
          ) : !hasMore && events.length > 0 ? (
            <Text style={styles.footerEnd}>
              {`Showing all ${events.length} of ${total}`}
            </Text>
          ) : null
        }
        renderItem={({ item: section }) => (
          <View style={styles.bucket}>
            <Text style={styles.bucketHeader}>{section.bucketLabel}</Text>
            {section.events.map((evt) => (
              <ActivityRow key={evt.id} event={evt} />
            ))}
          </View>
        )}
      />
    </View>
  );
};

/* ──────────────────────────────────────────────────────────────────────
 * Single row
 * ────────────────────────────────────────────────────────────────────── */

const ActivityRow: React.FC<{ event: GroupActivityEvent }> = ({ event }) => {
  const icon = actionIcon(event.action);
  const tone = actionTone(event.action);
  return (
    <View style={styles.row} testID={`activity-event-${event.id}`}>
      <View style={[styles.iconBubble, tone === 'overdue' && styles.iconBubbleOverdue]}>
        <MaterialIcons
          name={icon}
          size={16}
          color={tone === 'overdue' ? fpColors.error : fpColors.primary}
        />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={styles.line} numberOfLines={2}>
          {renderActorLine(event)}
        </Text>
        {event.note ? (
          <Text style={styles.subline} numberOfLines={2}>
            “{event.note}”
          </Text>
        ) : null}
        <Text style={styles.meta}>{formatRelative(event.created_at)}</Text>
      </View>
    </View>
  );
};

/* ──────────────────────────────────────────────────────────────────────
 * Helpers
 * ────────────────────────────────────────────────────────────────────── */

interface DayBucket {
  bucketKey: string;
  bucketLabel: string;
  events: GroupActivityEvent[];
}

function groupByDayBucket(events: GroupActivityEvent[]): DayBucket[] {
  const buckets = new Map<string, DayBucket>();
  for (const e of events) {
    const key = bucketKeyFor(e.created_at);
    if (!buckets.has(key)) {
      buckets.set(key, { bucketKey: key, bucketLabel: bucketLabelFor(e.created_at), events: [] });
    }
    buckets.get(key)!.events.push(e);
  }
  return Array.from(buckets.values());
}

function bucketKeyFor(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'unknown';
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

function bucketLabelFor(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(d)) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) {
    return d.toLocaleDateString(undefined, { weekday: 'long' });
  }
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diffMs = Date.now() - t;
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function actionIcon(action: CustodyAction): keyof typeof MaterialIcons.glyphMap {
  switch (action) {
    case 'HANDOFF':
      return 'arrow-forward';
    case 'RETURN':
    case 'LEND_BACK':
      return 'check-circle';
    case 'RECALL':
      return 'priority-high';
    case 'RECALL_ACK':
      return 'verified';
    case 'WAITLIST_JOIN':
      return 'queue';
    case 'WAITLIST_FULFILL':
      return 'check';
    case 'LEND_OUT':
      return 'send';
    case 'RECONCILE':
      return 'fact-check';
    case 'REMIND':
      return 'notifications';
    case 'RESET':
      return 'restore';
    default:
      return 'history';
  }
}

function actionTone(action: CustodyAction): 'neutral' | 'overdue' {
  return action === 'RECALL' ? 'overdue' : 'neutral';
}

function renderActorLine(event: GroupActivityEvent): string {
  const item = event.item_name ?? `item #${event.item_id}`;
  const actor = event.actor_user_name ?? 'Someone';
  const from = event.from_user_name;
  const to = event.to_user_name;

  switch (event.action) {
    case 'HANDOFF':
      if (to) return `${actor} handed ${item} to ${to}`;
      return `${actor} handed off ${item}`;
    case 'RETURN':
    case 'LEND_BACK':
      if (from) return `${actor} returned ${item} from ${from}`;
      return `${actor} returned ${item}`;
    case 'RECALL':
      if (from) return `${actor} recalled ${item} from ${from}`;
      return `${actor} recalled ${item}`;
    case 'RECALL_ACK':
      return `${actor} acknowledged the recall on ${item}`;
    case 'WAITLIST_JOIN':
      return `${actor} joined the waitlist for ${item}`;
    case 'WAITLIST_FULFILL':
      if (to) return `${item} fulfilled to ${to} from the waitlist`;
      return `Waitlist fulfilled for ${item}`;
    case 'LEND_OUT':
      if (to) return `${actor} lent ${item} to ${to}`;
      return `${actor} lent out ${item}`;
    case 'RECONCILE':
      return `${item} reconciled by ${actor}`;
    case 'REMIND':
      return `Reminder sent for ${item}`;
    case 'RESET':
      return `Custody reset on ${item} by ${actor}`;
    default:
      return `${actor} · ${item}`;
  }
}

/* ──────────────────────────────────────────────────────────────────────
 * Styles — mirrors GroupInventorySection so both tabs feel consistent
 * ────────────────────────────────────────────────────────────────────── */

const styles = StyleSheet.create({
  section: {
    marginTop: 12,
    flex: 1,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    marginBottom: 8,
  },
  sectionLabel: {
    fontSize: 14,
    fontWeight: '700',
    color: fpColors.onSurface,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  headerCount: {
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
  },
  list: {
    paddingHorizontal: 16,
    paddingBottom: 24,
  },
  bucket: {
    marginBottom: 14,
  },
  bucketHeader: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: fpColors.outline,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginBottom: 8,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 12,
    backgroundColor: fpColors.surfaceContainerLow,
    marginBottom: 6,
  },
  iconBubble: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: fpColors.primaryContainer,
    marginTop: 2,
  },
  iconBubbleOverdue: {
    backgroundColor: fpColors.errorContainer,
  },
  line: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 14,
    color: fpColors.onSurface,
  },
  subline: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
    marginTop: 2,
    fontStyle: 'italic',
  },
  meta: {
    fontFamily: 'Inter_400Regular',
    fontSize: 11,
    color: fpColors.outline,
    marginTop: 4,
  },
  skeletonWrap: {
    paddingHorizontal: 16,
  },
  skeletonRow: {
    height: 60,
    backgroundColor: fpColors.surfaceContainerLow,
    borderRadius: 12,
    marginBottom: 8,
    opacity: 0.6,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginHorizontal: 16,
    backgroundColor: fpColors.errorContainer,
    borderRadius: 10,
  },
  errorText: {
    flex: 1,
    fontSize: 13,
    color: fpColors.onSurface,
  },
  errorRetry: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    backgroundColor: fpColors.primary,
    borderRadius: 6,
  },
  errorRetryText: {
    fontSize: 12,
    color: fpColors.onPrimary,
    fontWeight: '600',
  },
  emptyWrap: {
    paddingHorizontal: 24,
    paddingVertical: 24,
    alignItems: 'center',
  },
  emptyEmoji: {
    fontSize: 36,
    marginBottom: 8,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: fpColors.onSurface,
    marginBottom: 4,
  },
  emptyBody: {
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
    lineHeight: 18,
  },
  footerLoading: {
    paddingVertical: 16,
    alignItems: 'center',
  },
  footerEnd: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.outline,
    textAlign: 'center',
    paddingVertical: 14,
  },
});

export default GroupActivityTab;
